import { useEffect, useState } from 'react';
import institutionService from '@/shared/services/institutionService';

// One fetch of the directory serves every caller on every page. The policy
// text is small and changes only when a coordinator saves, so a page that
// shows the capital-equipment notice and a form that reads its threshold must
// not each cost a request. A caller that arrives while the fetch is in flight
// joins it rather than starting another; a failed fetch is not cached, so the
// next caller retries.
let cache = null;
let inflight = null;
const subscribers = new Set();

function notify() {
    subscribers.forEach((listener) => listener());
}

export function loadInstitutionDirectory() {
    if (cache) {
        return Promise.resolve(cache);
    }
    if (!inflight) {
        inflight = institutionService
            .list()
            .then((directory) => {
                cache = directory;
                return directory;
            })
            .finally(() => {
                inflight = null;
            });
    }
    return inflight;
}

/**
 * Put a freshly saved institution into the cache and tell every mounted
 * caller, so the editor's own save is visible to the notice and the form
 * without another request. The admin editor calls this after each save.
 */
export function updateCachedInstitution(institution) {
    if (cache) {
        cache = cache.map((row) =>
            row.key === institution.key ? institution : row
        );
    } else {
        // Nothing to patch; the next reader fetches the saved state.
        inflight = null;
    }
    notify();
}

/** Drop the cache so the next reader fetches. Tests call this between cases. */
export function resetInstitutionDirectory() {
    cache = null;
    inflight = null;
    notify();
}

/**
 * The whole directory: `{ institutions, loading, error }`.
 */
export function useInstitutionDirectory() {
    const [state, setState] = useState({
        institutions: cache,
        loading: !cache,
        error: null,
    });

    useEffect(() => {
        let active = true;
        const sync = () => {
            if (active && cache) {
                setState({ institutions: cache, loading: false, error: null });
            }
        };
        subscribers.add(sync);
        if (cache) {
            sync();
        } else {
            loadInstitutionDirectory().then(
                () => sync(),
                (error) => {
                    if (active) {
                        setState({
                            institutions: null,
                            loading: false,
                            error,
                        });
                    }
                }
            );
        }
        return () => {
            active = false;
            subscribers.delete(sync);
        };
    }, []);

    return state;
}

/**
 * The policy in force for one institution key: `{ policy, loading, error }`.
 * `policy` is null until the directory arrives, when `key` is not an
 * institution, or when the read failed.
 */
export function useInstitutionPolicy(key) {
    const { institutions, loading, error } = useInstitutionDirectory();
    const row = institutions && institutions.find((i) => i.key === key);
    return { policy: row ? row.policy : null, loading, error };
}

export default useInstitutionPolicy;
